import React, { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import { Form, FormSubmit, TextInput } from '@dashboard/components/Formik';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import { ImageField } from '@dashboard/components/Formik/components/Image';
import { useTicketGroups } from '../TicketGroupsContext';
import { fromZonedTime } from 'date-fns-tz';
import { formatDateForInput } from '@/dashboard/services/formatting/date';
import handleAvailabilityBehaviorOnChange from '../handleAvailabilityBehaviorOnChange';
import {
  TicketTypeAvailabilityBehavior,
  TicketTypeAvailabilityStatus,
} from '@/dashboard/enums/tickets';
import * as Yup from 'yup';
import prepareTicketTypePricingValues from '../prepareTicketTypePricingValues';
import { getTicketFeeFormValues, prepareTicketFeeValues } from '../ticketFeeFormValues';
import Link from '@dashboard/components/Link';
import TicketTypeConditionalLogicField, {
  NO_TICKET_TYPE_RULE,
  prepareTicketTypeRule,
  toTicketTypeRuleFormValues,
} from './TicketTypeConditionalLogicField';

export default function TicketTypeEdit({
  ticketType,
  ticketGroupId,
  cancelButton,
  submitButton,
  formSubmitDirection,
}) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const { setHasUnsavedChanges, setIsAddingTicketType, setEditingTicketTypeId } =
    useTicketGroupEdit();
  const { onCancelEditTicketType } = useTicketTypeHelpers();
  const { onDirty } = useTicketTypeHelpers(ticketType?._id);
  // Ref (not state) so the mutation's onSuccess closure can read the current
  // value without re-creating the mutation, and so we can keep the form
  // mounted across the submit without a re-render flash.
  const shouldAddAnotherRef = useRef(false);

  const { state: ticketGroups, dispatch } = useTicketGroups();
  const ticketGroup = ticketGroups.find(tg => tg._id === ticketGroupId);
  const currencyCode = (page.eventDetails?.currency || '').toUpperCase();

  const defaultAvailabilityBehavior = page?.eventDetails?.startDate
    ? TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS
    : TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;

  const getSaleStartOnFromReference = triggerTicketType => {
    if (triggerTicketType == null || page?.eventDetails?.timezone == null) return null;

    const referencedTicketType = ticketGroups
      .flatMap(group => group.ticketTypes)
      .find(tt => tt._id === triggerTicketType);

    if (
      referencedTicketType?.availabilityBehavior ===
      TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS
    ) {
      if (page?.eventDetails?.startDate) {
        return formatDateForInput(page.eventDetails.startDate, page?.eventDetails?.timezone);
      }
      return null;
    }

    if (referencedTicketType?.saleEndOn) {
      return formatDateForInput(referencedTicketType.saleEndOn, page?.eventDetails?.timezone);
    }

    return null;
  };

  const editTicketTypeMutation = useMutation({
    mutationFn: values =>
      fetcher(
        ticketType?._id
          ? `/sellers/${sellerId}/ticket-types/${ticketType?._id}`
          : `/sellers/${sellerId}/ticket-types`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: values => {
      if (ticketType?._id) {
        dispatch({
          type: 'updateTicketType',
          ticketGroupId,
          ticketTypeId: ticketType._id,
          values,
        });
      } else {
        dispatch({ type: 'addTicketType', ticketGroupId, values });
      }

      // When "Add another" was clicked we keep the form mounted to avoid the
      // flicker from unmount/remount; the values are reset in place below.
      if (!shouldAddAnotherRef.current || ticketType?._id) {
        setIsAddingTicketType(false);
      }

      window.setTimeout(() => {
        setHasUnsavedChanges(false);
      }, 250);

      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    },
  });

  return (
    <Form
      onDirty={onDirty}
      validationSchema={Yup.object().shape({
        name: Yup.string().trim().required('Ticket name is required'),
        triggerTicketType: Yup.string().when('availabilityBehavior', {
          is: value =>
            [
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
            ].includes(value),
          then: Yup.string().nullable().required('Please select a ticket'),
          otherwise: Yup.string().nullable(),
        }),
        saleStartOn: Yup.string().when('availabilityBehavior', {
          is: TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
          then: Yup.string()
            .nullable()
            .test('date-required', 'A start or end date is required.', function (value) {
              const { saleEndOn } = this.parent;
              // Only show error on saleStartOn if both are empty
              if ((!value || value === '') && (!saleEndOn || saleEndOn === '')) {
                return false;
              }
              return true;
            }),
          otherwise: Yup.string().nullable(),
        }),
        saleEndOn: Yup.string().when('availabilityBehavior', {
          is: TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
          then: Yup.string().nullable(),
          otherwise: Yup.string().nullable(),
        }),
      })}
      initialValues={{
        availabilityStatus:
          ticketType?.availabilityStatus || TicketTypeAvailabilityStatus.AVAILABLE,
        image: ticketType?.image,
        name: ticketType?.name || '',
        description: ticketType?.description || '',
        capacity: ticketType?.capacity === null ? null : ticketType?.capacity,
        pricing: ticketType?.pricing || 'paid',
        price: ticketType?.price
          ? formatPriceForInput({
              amount: ticketType.price,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        discountedFromPrice: ticketType?.discountedFromPrice
          ? formatPriceForInput({
              amount: ticketType.discountedFromPrice,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        pwywSuggestedPrice: ticketType?.pwywSuggestedPrice
          ? formatPriceForInput({
              amount: ticketType.pwywSuggestedPrice,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        ...getTicketFeeFormValues(ticketType, page.eventDetails?.currency),
        minQuantity: ticketType?.minQuantity || 0,

        maxQuantity: ticketType?.maxQuantity || 10,
        showAvailableQuantity: ticketType?.showAvailableQuantity === false ? false : true,
        showTicketSaleDates: ticketType?.showTicketSaleDates === false ? false : true,
        hidden: ticketType?.hidden || false,
        hideWhenSoldOut: ticketType?.hideWhenSoldOut || false,
        hideWhenNotOnSale: ticketType?.hideWhenNotOnSale || false,
        hideWhenScheduled: ticketType?.hideWhenScheduled || false,
        hideWhenUnavailable: ticketType?.hideWhenUnavailable || false,
        reference: ticketType?.reference || '',
        saleStartOn: formatDateForInput(ticketType?.saleStartOn, page?.eventDetails?.timezone),
        saleEndOn:
          ticketType != null
            ? formatDateForInput(ticketType?.saleEndOn, page?.eventDetails?.timezone)
            : formatDateForInput(
                ticketType?.saleEndOn ||
                  (ticketType?.availabilityBehavior ===
                    TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS ||
                  defaultAvailabilityBehavior === TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS
                    ? page.eventDetails?.startDate
                    : null),
                page?.eventDetails?.timezone,
              ),
        triggerTicketType: ticketType?.triggerTicketType,
        availabilityBehavior: ticketType?.availabilityBehavior || defaultAvailabilityBehavior,
        showHideLogic: toTicketTypeRuleFormValues(ticketType?.showHideLogic),
      }}
      onSubmit={async (values, actions) => {
        try {
          const pricingValues = prepareTicketTypePricingValues(values, page.eventDetails?.currency);

          if (pricingValues.error) {
            return { error: pricingValues.error };
          }

          const { price, discountedFromPrice, pwywSuggestedPrice } = pricingValues;

          const feeValues = prepareTicketFeeValues(values, page.eventDetails?.currency);

          if (feeValues.error) {
            return { error: feeValues.error };
          }

          if (!values.minQuantity) {
            values.minQuantity = 0;
          }

          if (!values.maxQuantity) {
            values.maxQuantity = 10;
          }

          if (
            values.availabilityBehavior === TicketTypeAvailabilityBehavior.DATE_TIME_BASED &&
            (values.saleStartOn == null || values.saleStartOn === '') &&
            (values.saleEndOn == null || values.saleEndOn === '')
          ) {
            values.saleEndOn = null;
            values.saleStartOn = null;
            values.availabilityBehavior = TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;
          }

          if (values.triggerTicketType == '') {
            values.triggerTicketType = null;
          }

          if (
            [
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
            ].includes(values.availabilityBehavior) &&
            (values.triggerTicketType == null || values.triggerTicketType === '')
          ) {
            values.availabilityBehavior = TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;
          }

          const showHideLogic = prepareTicketTypeRule(
            values.showHideLogic,
            ticketGroups.flatMap(group => group.ticketTypes),
          );

          const data = await editTicketTypeMutation.mutateAsync({
            ...values,
            showHideLogic,
            feeAmount: feeValues.feeAmount,
            saleStartOn: values.saleStartOn
              ? fromZonedTime(values.saleStartOn, page?.eventDetails?.timezone)
              : null,
            saleEndOn: values.saleEndOn
              ? fromZonedTime(values.saleEndOn, page?.eventDetails?.timezone)
              : null,
            triggerTicketType: values.triggerTicketType === '' ? null : values.triggerTicketType,
            price,
            discountedFromPrice,
            pwywSuggestedPrice,
            pageId: page._id,
            ticketGroupId,
          });

          if (!ticketType?._id) {
            // if new ticket type, reset form
            actions.resetForm();
          } else {
            // if editing, reset form with new values
            actions.resetForm({
              values: {
                availabilityStatus: data.availabilityStatus,
                image: data.image,
                name: data.name,
                description: data.description,
                capacity: data.capacity,
                pricing: data.pricing,
                price:
                  data.pricing === 'paid' || data.pricing === 'pwyw'
                    ? formatPriceForInput({
                        amount: data.price,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                discountedFromPrice:
                  data.pricing === 'paid' && data.discountedFromPrice
                    ? formatPriceForInput({
                        amount: data.discountedFromPrice,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                pwywSuggestedPrice:
                  data.pricing === 'pwyw' && data.pwywSuggestedPrice
                    ? formatPriceForInput({
                        amount: data.pwywSuggestedPrice,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                ...getTicketFeeFormValues(data, page.eventDetails?.currency),
                minQuantity: data.minQuantity,

                maxQuantity: data.maxQuantity,
                showAvailableQuantity: data.showAvailableQuantity,
                showTicketSaleDates: data.showTicketSaleDates,
                hidden: data.hidden,
                hideWhenSoldOut: data.hideWhenSoldOut,
                hideWhenNotOnSale: data.hideWhenNotOnSale,
                hideWhenScheduled: data.hideWhenScheduled,
                hideWhenUnavailable: data.hideWhenUnavailable,
                reference: data.reference,
                saleStartOn: formatDateForInput(data?.saleStartOn, page?.eventDetails?.timezone),
                saleEndOn: formatDateForInput(data?.saleEndOn, page?.eventDetails?.timezone),
                triggerTicketType: data?.triggerTicketType,
                availabilityBehavior: data?.availabilityBehavior || defaultAvailabilityBehavior,
                showHideLogic: toTicketTypeRuleFormValues(data?.showHideLogic ?? showHideLogic),
              },
            });
          }

          return {
            data: data,
            onSuccess: () => {
              if (!ticketType?._id && shouldAddAnotherRef.current) {
                shouldAddAnotherRef.current = false;
                setEditingTicketTypeId(null);
                // Form is already mounted and reset via actions.resetForm()
                // above. Focus the name input so the user can immediately
                // type the next ticket — autoFocus only fires on mount.
                window.setTimeout(() => {
                  document.getElementById('name')?.focus();
                }, 0);
              }
            },
          };
        } catch (error) {
          return {
            error: error,
          };
        }
      }}
      inputClassName="text-sm"
      fields={({ values, setFieldValue }) => {
        const availableTicketTypeOptions = ticketGroups.flatMap(group =>
          group.ticketTypes
            .filter(tt => tt._id !== ticketType?._id)
            .map(tt => ({
              label: tt.name,
              value: tt._id,
            })),
        );

        const isCapacityReached =
          (values.capacity != null &&
            values.capacity != '' &&
            ticketType?.quantitySold != null &&
            ticketType?.quantitySold >= values.capacity) ||
          values.capacity === 0;

        return [
          {
            type: 'custom',
            renderField: () => {
              return (
                <div className="flex gap-x-2 relative">
                  <div className="w-[63px] h-[63px]">
                    <ImageField
                      id="image"
                      name="image"
                      image={values.image}
                      previewSize="variant_option"
                      variant="minimal"
                    />
                  </div>

                  <div className="flex-1">
                    <TextInput
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Name"
                      className="rounded-b-none focus:z-10 !py-[6px] relative text-sm"
                      autoComplete="off"
                      required
                      border
                      autoFocus={!values.name}
                    />

                    <TextInput
                      id="description"
                      name="description"
                      className="border-t-0 rounded-t-none -mt-px focus:z-10 !py-[6px] relative text-sm"
                      type="text"
                      placeholder="Short description"
                      border
                    />
                  </div>
                </div>
              );
            },
            reduceMargin: true,
          },
          {
            type: 'columns',
            columns: 1,
            className: '!gap-y-3',
            gap: 'sm',
            fields: [
              {
                name: 'pricing',
                label: 'Pricing',
                type: 'radioCard',
                columns: 2,
                options: [
                  { label: 'Fixed price', value: 'paid' },
                  { label: 'Pay what you want', value: 'pwyw' },
                  { label: 'Free', value: 'free' },
                ],
                reduceMargin: true,
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'price',
                    label: values.pricing === 'pwyw' ? 'Minimum price' : 'Price',
                    type: 'number',
                    required: values.pricing === 'paid',
                    suffix: currencyCode,
                    reduceMargin: true,
                  },
                  values.pricing === 'pwyw'
                    ? {
                        name: 'pwywSuggestedPrice',
                        label: 'Suggested price',
                        type: 'number',
                        suffix: currencyCode,
                        reduceMargin: true,
                      }
                    : {
                        name: 'discountedFromPrice',
                        label: 'Discounted from',
                        type: 'number',
                        suffix: currencyCode,
                        reduceMargin: true,
                      },
                ],
                visible: values.pricing !== 'free',
                noMargin: true,
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                noMargin: true,
                fields: [
                  {
                    name: 'feeAmount',
                    label: 'Booking fee',
                    type: 'number',
                    placeholder: 'No fee',
                    suffix: currencyCode,
                    tooltip: 'Charged once per ticket, on top of the price.',
                    reduceMargin: true,
                  },
                ],
              },
              {
                type: 'divider',
                reduceMargin: true,
              },

              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'availabilityStatus',
                    type: 'select',
                    label: 'Sales status',
                    tooltip:
                      isCapacityReached &&
                      `Disabled when tickets sold (currently ${ticketType?.quantitySold ?? 0} sold) exceeds ticket availability. Increase availability to change ticket availability status.`,
                    options: [
                      {
                        label: 'Select a ticket availability',
                        value: '',
                        disabled: true,
                      },
                      {
                        label: 'On sale',
                        value: TicketTypeAvailabilityStatus.AVAILABLE,
                        disabled: isCapacityReached,
                      },
                      {
                        label: 'Unavailable',
                        value: TicketTypeAvailabilityStatus.UNAVAILABLE,
                        disabled: isCapacityReached,
                      },
                      {
                        label: 'Sold out',
                        value: TicketTypeAvailabilityStatus.SOLD_OUT,
                      },
                    ],
                    reduceMargin: true,
                  },
                  {
                    name: 'availabilityBehavior',
                    label: 'Sales start',
                    type: 'select',
                    options: [
                      {
                        label: 'Now until event starts',
                        value: TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS,
                      },
                      {
                        label: 'Always',
                        value: TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE,
                      },
                      {
                        label: 'Date & time',
                        value: TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
                      },
                      {
                        label: 'When another ticket sale ends',
                        value: TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                      },
                      {
                        label: 'When another ticket sells out',
                        value: TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                      },
                      {
                        label: 'When another ticket sells out or sale ends',
                        value: TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                      },
                    ],
                    reduceMargin: true,
                    onChange: e => {
                      handleAvailabilityBehaviorOnChange({
                        triggerKey: 'triggerTicketType',
                        value: e.target?.value,
                        setFieldValue,
                        page,
                        values,
                        getSaleStartOnFromReference,
                      });
                    },
                  },
                ],
              },
              {
                name: 'triggerTicketType',
                type: 'select',
                label: 'Ticket',
                options: [
                  {
                    label: 'Select a ticket',
                    value: '',
                  },
                  ...availableTicketTypeOptions,
                ],
                visible: values =>
                  [
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                  ].includes(values.availabilityBehavior),
                reduceMargin: true,
                onChange: e => {
                  if (e.target?.value) {
                    const saleStartOn = getSaleStartOnFromReference(e.target?.value);
                    if (saleStartOn) {
                      setFieldValue('saleStartOn', saleStartOn);
                    } else {
                      setFieldValue('saleStartOn', '');
                    }
                  }

                  if (e.target?.value == '') {
                    setFieldValue('saleStartOn', '');
                    setFieldValue('saleEndOn', '');
                  }

                  setFieldValue('triggerTicketType', e.target.value);
                },
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'saleStartOn',
                    label: 'Start sale on',
                    placeholder: 'Start sale on',
                    type: 'datetime-local',
                    reduceMargin: true,
                    disabled: [
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                    ].includes(values.availabilityBehavior),
                  },
                  {
                    name: 'saleEndOn',
                    label: 'End sale on',
                    placeholder: 'End sale on',
                    type: 'datetime-local',
                    reduceMargin: true,
                  },
                ],
                visible: values => {
                  if (
                    values.availabilityBehavior === TicketTypeAvailabilityBehavior.DATE_TIME_BASED
                  ) {
                    return true;
                  }

                  return (
                    [
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                    ].includes(values.availabilityBehavior) &&
                    (values.triggerTicketType != null || values.triggerTicketType != '')
                  );
                },
              },
              {
                name: 'saleEndOn',
                label: 'End sale on',
                placeholder: 'End sale on',
                type: 'datetime-local',
                reduceMargin: true,
                visible: values =>
                  values.availabilityBehavior ===
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT &&
                  (values.triggerTicketType != null || values.triggerTicketType != ''),
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                reduceMargin: true,
                fields: [
                  {
                    name: 'capacity',
                    label: 'Quantity available',
                    placeholder: 'Quantity available',
                    type: 'number',
                    reduceMargin: true,
                    onChange: e => {
                      setFieldValue('capacity', e.target?.value);
                      const capacity = parseInt(e.target?.value, 10);

                      if (
                        capacity === 0 ||
                        (!isNaN(capacity) &&
                          ticketType?.quantitySold != null &&
                          ticketType?.quantitySold >= e.target?.value)
                      ) {
                        setFieldValue('availabilityStatus', TicketTypeAvailabilityStatus.SOLD_OUT);
                      }
                    },
                  },
                  {
                    type: 'columns',
                    columns: 2,
                    visible: ticketGroup.ticketSelectionType === 'quantity',
                    gap: 'sm',
                    fields: [
                      {
                        name: 'minQuantity',
                        label: 'Min per order',
                        type: 'number',
                        min: 0,
                        max: 999,
                        reduceMargin: true,
                      },
                      {
                        name: 'maxQuantity',
                        label: 'Max per order',
                        type: 'number',
                        min: 1,
                        max: 999,
                        reduceMargin: true,
                      },
                    ],
                    noMargin: true,
                  },
                ],
              },
              {
                type: 'disclosure',
                label: 'Additional settings',
                nested: true,
                fields: [
                  {
                    name: 'showHideLogic.enabled',
                    label: 'Conditional logic',
                    type: 'checkbox',
                    tooltip: (
                      <>
                        Show this ticket based on another ticket&apos;s selection.{' '}
                        <Link
                          to="/docs/articles/conditional-logic-for-ticket-types"
                          target="_blank"
                          variant="light"
                        >
                          Learn more
                        </Link>
                      </>
                    ),
                    reduceMargin: true,
                    // Unticking clears the rule, so reticking starts from an empty one
                    // rather than bringing back tickets nobody picked this time.
                    onChange: value => {
                      if (!value) setFieldValue('showHideLogic', NO_TICKET_TYPE_RULE);
                    },
                  },
                  ...(values.showHideLogic?.enabled
                    ? [
                        {
                          name: 'showHideLogic',
                          type: 'custom',
                          inset: true,
                          renderField: () => (
                            <TicketTypeConditionalLogicField
                              ticketGroups={ticketGroups}
                              ticketTypeId={ticketType?._id}
                            />
                          ),
                        },
                      ]
                    : []),
                  {
                    type: 'fieldgroup',
                    label: 'Hide manually',
                    fields: [
                      {
                        label: 'Hide this ticket',
                        name: 'hidden',
                        type: 'checkbox',
                        tooltip:
                          'Hide this ticket. You can show hidden tickets using query parameters',
                      },
                    ],
                    reduceMargin: true,
                  },
                  {
                    type: 'fieldgroup',
                    label: 'Hide automatically when',
                    fields: [
                      {
                        label: 'Scheduled',
                        name: 'hideWhenScheduled',
                        type: 'checkbox',
                        tooltip: 'Hide this ticket when it is scheduled to go on sale',
                      },
                      {
                        label: 'Not on sale',
                        name: 'hideWhenNotOnSale',
                        type: 'checkbox',
                        tooltip: 'Hide this ticket after the sale end date',
                      },
                      {
                        label: 'Unavailable',
                        name: 'hideWhenUnavailable',
                        type: 'checkbox',
                        tooltip: 'Hide this ticket when it is unavailable',
                      },
                      {
                        label: 'Sold out',
                        name: 'hideWhenSoldOut',
                        type: 'checkbox',
                        tooltip: 'Hide this ticket when it is sold out',
                      },
                    ],
                    reduceMargin: true,
                  },
                  {
                    type: 'fieldgroup',
                    label: 'Show on ticket',
                    fields: [
                      {
                        name: 'showAvailableQuantity',
                        label: 'Quantity available',
                        type: 'checkbox',
                      },
                      {
                        name: 'showTicketSaleDates',
                        label: 'Sale dates',
                        type: 'checkbox',
                      },
                    ],
                    reduceMargin: true,
                  },
                  {
                    name: 'reference',
                    label: 'Reference',
                    type: 'text',
                    tooltip:
                      'Used to identify this ticket in Zapier, webhooks, and query parameters.',
                    noMargin: true,
                  },
                ],
              },
            ],
          },
        ];
      }}
      formSubmit={
        ticketType?._id
          ? null
          : () => {
              if (!ticketType?._id) {
                return (
                  <FormSubmit>
                    <div className="flex flex-row justify-between w-full items-center">
                      <div>
                        {(ticketGroups.length > 1 || ticketGroup?.ticketTypes?.length > 0) && (
                          <FormSubmit.Cancel
                            appearance="secondary"
                            onClick={onCancelEditTicketType}
                          >
                            Cancel
                          </FormSubmit.Cancel>
                        )}
                      </div>

                      <div className="flex flex-row gap-x-4">
                        <FormSubmit.Button
                          appearance="secondary"
                          onClick={() => {
                            shouldAddAnotherRef.current = true;
                          }}
                        >
                          + Add another
                        </FormSubmit.Button>
                        <FormSubmit.Button>Save</FormSubmit.Button>
                      </div>
                    </div>
                  </FormSubmit>
                );
              }
            }
      }
      formSubmitDirection={formSubmitDirection}
      formSubmitAlign="end"
      cancelButton={
        cancelButton
          ? cancelButton
          : {
              label: 'Cancel',
              onClick: onCancelEditTicketType,
            }
      }
      submitButton={submitButton}
      showState
    />
  );
}
